import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { toPersianDigits } from '../../../../utils/convertNumbers';

// A deliberately small Markdown subset for assistant replies: paragraphs,
// headings, bullet/numbered lists, pipe tables, **bold** and `code`.
// Everything becomes React elements — model output is never injected as HTML
// and no link is built from free text. Old plain-text replies render as
// paragraphs, exactly as before. Digits are converted for display only.

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+|[۰-۹]+)[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,4}\s+(.*)$/;

const splitRow = (line) =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

function parseBlocks(text) {
    const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i += 1;
            continue;
        }
        if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1])) {
            const header = splitRow(line);
            const rows = [];
            i += 2;
            while (i < lines.length && TABLE_ROW.test(lines[i])) {
                rows.push(splitRow(lines[i]));
                i += 1;
            }
            blocks.push({ type: 'table', header, rows });
            continue;
        }
        const heading = line.match(HEADING);
        if (heading) {
            blocks.push({ type: 'heading', text: heading[1] });
            i += 1;
            continue;
        }
        if (BULLET.test(line) || NUMBERED.test(line)) {
            const ordered = !BULLET.test(line);
            const items = [];
            while (i < lines.length && (ordered ? NUMBERED.test(lines[i]) : BULLET.test(lines[i]))) {
                const match = lines[i].match(ordered ? NUMBERED : BULLET);
                items.push(ordered ? match[2] : match[1]);
                i += 1;
            }
            blocks.push({ type: ordered ? 'ol' : 'ul', items });
            continue;
        }
        const para = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !HEADING.test(lines[i]) &&
            !BULLET.test(lines[i]) &&
            !NUMBERED.test(lines[i]) &&
            !(TABLE_ROW.test(lines[i]) && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1]))
        ) {
            para.push(lines[i]);
            i += 1;
        }
        blocks.push({ type: 'p', lines: para });
    }
    return blocks;
}

// **bold** and `code`; anything else stays literal text.
function Inline({ text }) {
    const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            return <strong key={index}>{toPersianDigits(part.slice(2, -2))}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
            return (
                <Box component="code" key={index} sx={{ fontFamily: 'monospace', fontSize: '0.85em', direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {part.slice(1, -1)}
                </Box>
            );
        }
        return <React.Fragment key={index}>{toPersianDigits(part)}</React.Fragment>;
    });
}

// Prose keeps a readable measure; tables (below) may use the full width.
const textSx = { lineHeight: 1.9, unicodeBidi: 'plaintext', overflowWrap: 'anywhere', maxWidth: '75ch' };

function AssistantMarkdown({ text }) {
    const blocks = parseBlocks(text);
    return (
        <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
            {blocks.map((block, index) => {
                if (block.type === 'heading') {
                    return (
                        <Typography key={index} variant="body2" component="h3" sx={{ ...textSx, fontWeight: 700, mt: 1.5, mb: 0.5, fontSize: '0.95rem' }}>
                            <Inline text={block.text} />
                        </Typography>
                    );
                }
                if (block.type === 'ul' || block.type === 'ol') {
                    return (
                        <Box key={index} component={block.type} sx={{ my: 0.75, pr: 2.5, pl: 0 }}>
                            {block.items.map((item, itemIndex) => (
                                <Typography key={itemIndex} component="li" variant="body2" sx={{ ...textSx, mb: 0.25 }}>
                                    <Inline text={item} />
                                </Typography>
                            ))}
                        </Box>
                    );
                }
                if (block.type === 'table') {
                    const width = Math.max(block.header.length, ...block.rows.map((r) => r.length));
                    const pad = (row) => [...row, ...Array(Math.max(0, width - row.length)).fill('')];
                    return (
                        // Wide tables scroll inside themselves, never the page.
                        <TableContainer
                            key={index}
                            sx={{ my: 1, overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1.5, maxWidth: '100%' }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {pad(block.header).map((cell, c) => (
                                            <TableCell key={c} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'action.hover' }}>
                                                <Inline text={cell} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {block.rows.map((row, r) => (
                                        <TableRow key={r}>
                                            {pad(row).map((cell, c) => (
                                                <TableCell key={c} sx={{ unicodeBidi: 'plaintext' }}>
                                                    <Inline text={cell} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }
                return (
                    <Typography key={index} variant="body2" component="p" sx={{ ...textSx, my: 0.75 }}>
                        {block.lines.map((line, l) => (
                            <React.Fragment key={l}>
                                {l > 0 && <br />}
                                <Inline text={line} />
                            </React.Fragment>
                        ))}
                    </Typography>
                );
            })}
        </Box>
    );
}

export default AssistantMarkdown;
